/**
 * @fileoverview Card displaying ad platform integrations (Facebook, Google Ads).
 * @module features/settings/components/AdPlatformsCard
 */

import { ReactElement, useState, useRef, useEffect } from 'react'
import { formatDistanceToNow, parseISO, subDays } from 'date-fns'
import { pl, enUS } from 'date-fns/locale'
import { Megaphone, AlertTriangle, RefreshCw, Unplug, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { z } from 'zod'
import type { DateRange } from 'react-day-picker'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Skeleton,
  Button,
  ConfirmPopover,
  DateRangePicker,
} from '@/shared/components/ui'
import { cn } from '@/shared/utils/cn'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import apiClient from '@/lib/apiClient'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import { useIntegrationsStatus } from '../hooks/useIntegrationsStatus'
import { disconnectFacebook } from '../api/facebookOAuthService'
import { disconnectGoogleAds } from '../api/googleAdsOAuthService'
import { FacebookOAuthConnect } from './FacebookOAuthConnect'
import { GoogleAdsOAuthConnect } from './GoogleAdsOAuthConnect'
import type { IntegrationStatus, IntegrationProvider } from '../schemas/integrationStatusSchema'
import {
  STATUS_BADGE_VARIANTS,
  STATUS_LABEL_KEYS,
  PROVIDER_NAMES,
  SYNC_URLS,
  SYNC_JOB_STATUS_URLS,
} from './integration-shared'
import { getProviderIcon } from './integration-shared'
import { MetaCsvImportAction } from '@/features/ad-spends'
import { useExpandableEntry } from './integration-shared'

type SyncProvider = 'FACEBOOK' | 'GOOGLE_ADS'
type SyncJobState = 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED'

/** Legacy response schema for sync endpoints */
const legacySyncResponseSchema = z.union([
  z.string().transform((s) => ({ message: s })),
  z.object({ message: z.string().optional() }).passthrough(),
])

/** Accepted async sync job response schema */
const syncJobAcceptedSchema = z.object({
  jobId: z.string().uuid(),
  status: z.enum(['PENDING', 'RUNNING', 'SUCCESS', 'FAILED']),
  provider: z.string(),
  operation: z.string(),
  submittedAt: z.string(),
  message: z.string().optional(),
})

/** Polled async sync job status schema */
const syncJobStatusSchema = z.object({
  jobId: z.string().uuid(),
  status: z.enum(['PENDING', 'RUNNING', 'SUCCESS', 'FAILED']),
  provider: z.string(),
  operation: z.string(),
  submittedAt: z.string(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  recordsProcessed: z.number().int().nullable().optional(),
  message: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
})

const syncTriggerResponseSchema = z.union([syncJobAcceptedSchema, legacySyncResponseSchema])

type SyncJobAccepted = z.infer<typeof syncJobAcceptedSchema>
type SyncJobStatus = z.infer<typeof syncJobStatusSchema>

const isSyncJobAccepted = (data: unknown): data is SyncJobAccepted =>
  syncJobAcceptedSchema.safeParse(data).success

/** Ad platform providers */
const AD_PLATFORM_PROVIDERS: IntegrationProvider[] = ['FACEBOOK', 'GOOGLE_ADS']

interface AdPlatformEntryProps {
  integration: IntegrationStatus
  isExpanded: boolean
  onToggle: () => void
}

/**
 * Single ad platform entry component.
 */
function AdPlatformEntry({ integration, isExpanded, onToggle }: AdPlatformEntryProps): ReactElement {
  const { t, i18n } = useTranslation('features-settings')
  const [syncDateRange, setSyncDateRange] = useState<DateRange | undefined>(() => ({
    from: subDays(new Date(), 3),
    to: new Date(),
  }))

  const { contentRef, animatedHeight } = useExpandableEntry(isExpanded, [syncDateRange])
  const queryClient = useQueryClient()
  const { activeOrgId } = useActiveOrg()
  const [syncJob, setSyncJob] = useState<SyncJobStatus | null>(null)
  const pollIntervalRef = useRef<number | null>(null)
  const activeJobIdRef = useRef<string | null>(null)
  const provider = integration.provider as SyncProvider

  const stopSyncJobPolling = (): void => {
    if (pollIntervalRef.current) {
      window.clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    activeJobIdRef.current = null
  }

  const isTerminalSyncStatus = (status: SyncJobState): boolean =>
    status === 'SUCCESS' || status === 'FAILED'

  const parseSyncError = (error: unknown): string => {
    if (error instanceof Error && error.message) return error.message
    return t('cards.integrations.sync.syncFailed', { defaultValue: 'Sync failed' })
  }

  const fetchSyncJobStatus = async (jobId: string): Promise<SyncJobStatus> => {
    const baseUrl = SYNC_JOB_STATUS_URLS[provider]
    const res = await apiClient.get(`${baseUrl}/${jobId}`)
    return syncJobStatusSchema.parse(res.data)
  }

  const startSyncJobPolling = (jobId: string): void => {
    stopSyncJobPolling()
    activeJobIdRef.current = jobId

    const poll = async (): Promise<void> => {
      try {
        const status = await fetchSyncJobStatus(jobId)
        if (activeJobIdRef.current !== jobId) return
        setSyncJob(status)

        if (!isTerminalSyncStatus(status.status)) return
        stopSyncJobPolling()

        await queryClient.invalidateQueries({
          queryKey: activeOrgId ? ['org', activeOrgId, 'statistics'] : ['statistics'],
        })
        await queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey.includes('integrations-status'),
        })

        if (status.status === 'SUCCESS') {
          const recordsSuffix =
            status.recordsProcessed != null
              ? ` — ${t('cards.integrations.sync.records', { count: status.recordsProcessed })}`
              : ''
          toast.success(
            `${t('cards.integrations.sync.syncCompleted', { defaultValue: 'Sync completed' })}${recordsSuffix}`
          )
        } else {
          toast.error(
            status.error ||
              status.message ||
              t('cards.integrations.sync.syncFailed', { defaultValue: 'Sync failed' })
          )
        }
      } catch (error) {
        if (activeJobIdRef.current !== jobId) return
        stopSyncJobPolling()
        toast.error(parseSyncError(error))
      }
    }

    void poll()
    pollIntervalRef.current = window.setInterval(() => {
      void poll()
    }, 2000)
  }

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        window.clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      activeJobIdRef.current = null
    }
  }, [])

  // Facebook disconnect mutation
  const disconnectFacebookMutation = useMutation({
    mutationFn: async () => {
      if (!activeOrgId) throw new Error('No active organization')
      await disconnectFacebook(OrgIdSchema.parse(activeOrgId))
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey.includes('integrations-status'),
      })
    },
    onError: (error: Error) => {
      toast.error(
        error.message ||
          t('cards.integrations.facebook.disconnectError', {
            defaultValue: 'Failed to disconnect Facebook Ads',
          })
      )
    },
  })

  // Google Ads disconnect mutation
  const disconnectGoogleAdsMutation = useMutation({
    mutationFn: async () => {
      await disconnectGoogleAds()
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey.includes('integrations-status'),
      })
    },
    onError: (error: Error) => {
      toast.error(
        error.message ||
          t('cards.integrations.google.disconnectError', {
            defaultValue: 'Failed to disconnect Google Ads',
          })
      )
    },
  })

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async (range: DateRange) => {
      if (!range.from || !range.to) throw new Error('Date range required')
      const startDate = toDateOnlyStringLocal(range.from)
      const endDate = toDateOnlyStringLocal(range.to)
      const baseUrl = SYNC_URLS[provider]
      const url = `${baseUrl}?startDate=${startDate}&endDate=${endDate}`
      const res = await apiClient.post(url, {})
      return syncTriggerResponseSchema.parse(res.data)
    },
    onSuccess: async (data) => {
      if (isSyncJobAccepted(data)) {
        const accepted: SyncJobAccepted = data
        setSyncJob({
          jobId: accepted.jobId,
          status: accepted.status,
          provider: accepted.provider,
          operation: accepted.operation,
          submittedAt: accepted.submittedAt,
          startedAt: null,
          completedAt: null,
          recordsProcessed: null,
          message: accepted.message ?? null,
          error: null,
        })
        startSyncJobPolling(accepted.jobId)
        toast.success(
          t('cards.integrations.sync.syncTriggered', {
            provider: PROVIDER_NAMES[integration.provider],
            defaultValue: 'Sync started',
          })
        )
        return
      }

      await queryClient.invalidateQueries({
        queryKey: activeOrgId ? ['org', activeOrgId, 'statistics'] : ['statistics'],
      })
      const msg = data.message ? ` — ${data.message}` : ''
      toast.success(
        `${t('cards.integrations.sync.syncTriggered', { provider: PROVIDER_NAMES[integration.provider] })}${msg}`
      )
    },
    onError: (err) => {
      const error = err as { response?: { status?: number; data?: unknown } }
      const status = error.response?.status
      const detail =
        typeof error.response?.data === 'string' ? `: ${error.response.data.slice(0, 300)}` : ''
      toast.error(
        `${t('cards.integrations.sync.syncFailed')}${status ? ` (HTTP ${status})` : ''}${detail}`
      )
    },
  })

  const dateLocale = i18n.language === 'pl' ? pl : enUS
  const statusVariant = STATUS_BADGE_VARIANTS[integration.status] ?? STATUS_BADGE_VARIANTS.NOT_CONFIGURED
  const statusLabelKey = STATUS_LABEL_KEYS[integration.status] ?? STATUS_LABEL_KEYS.NOT_CONFIGURED
  const displayName = integration.accountName ?? PROVIDER_NAMES[integration.provider]

  const lastSyncedLabel = integration.lastSyncedAt
    ? t('cards.integrations.sync.lastSynced', {
        time: formatDistanceToNow(parseISO(integration.lastSyncedAt), {
          addSuffix: true,
          locale: dateLocale,
        }),
      })
    : t('cards.integrations.sync.neverSynced')

  const hasError = integration.status === 'ERROR' && integration.lastSyncError
  const isNotConfigured = integration.status === 'NOT_CONFIGURED'
  const canSync = integration.status === 'ACTIVE' || integration.status === 'ERROR'
  const canDisconnect = !isNotConfigured
  const isSyncJobRunning = syncJob?.status === 'PENDING' || syncJob?.status === 'RUNNING'
  const syncJobStatusLabel = syncJob
    ? syncJob.status === 'PENDING'
      ? t('cards.integrations.sync.jobQueued', { defaultValue: 'Queued' })
      : syncJob.status === 'RUNNING'
        ? t('cards.integrations.sync.jobRunning', { defaultValue: 'Running' })
        : syncJob.status === 'SUCCESS'
          ? t('cards.integrations.sync.jobSuccess', { defaultValue: 'Completed' })
          : t('cards.integrations.sync.jobFailed', { defaultValue: 'Failed' })
    : null
  const syncJobStatusVariant = syncJob
    ? syncJob.status === 'FAILED'
      ? 'destructive'
      : syncJob.status === 'SUCCESS'
        ? 'default'
        : 'outline'
    : 'outline'

  const handleSync = (): void => {
    if (!syncDateRange?.from || !syncDateRange?.to) return
    syncMutation.mutate(syncDateRange)
  }

  const renderConnectAction = (): ReactElement | null => {
    if (!isNotConfigured) return null

    switch (integration.provider) {
      case 'FACEBOOK':
        return <FacebookOAuthConnect />
      case 'GOOGLE_ADS':
        return <GoogleAdsOAuthConnect />
      default:
        return null
    }
  }

  const disconnectMutation =
    integration.provider === 'FACEBOOK' ? disconnectFacebookMutation : disconnectGoogleAdsMutation
  const disconnectTitle =
    integration.provider === 'FACEBOOK'
      ? t('cards.integrations.facebook.disconnectTitle', { defaultValue: 'Disconnect Facebook Ads?' })
      : t('cards.integrations.google.disconnectTitle', { defaultValue: 'Disconnect Google Ads?' })
  const disconnectDescription =
    integration.provider === 'FACEBOOK'
      ? t('cards.integrations.facebook.disconnectDescription', {
          defaultValue: 'This will remove your Facebook Ads integration. You can reconnect at any time.',
        })
      : t('cards.integrations.google.disconnectDescription', {
          defaultValue: 'This will remove your Google Ads integration. You can reconnect at any time.',
        })

  return (
    <div
      className="flex flex-col rounded-lg border border-border bg-card p-4 cursor-pointer transition-[filter] hover:brightness-105"
      onClick={onToggle}
    >
      {/* Info row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
            {getProviderIcon(integration.provider)}
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-foreground truncate">{displayName}</span>
              {!isNotConfigured && (
                <Badge
                  variant={statusVariant}
                  className={cn(
                    statusVariant === 'default' &&
                      'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
                    statusVariant === 'outline' &&
                      'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30'
                  )}
                >
                  {t(statusLabelKey)}
                </Badge>
              )}
              {isSyncJobRunning && (
                <Badge variant="outline" className="gap-1 text-muted-foreground">
                  <RefreshCw className="size-3 animate-spin" />
                  {t('cards.integrations.sync.syncing', { defaultValue: 'Syncing...' })}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
              {integration.accountId && <span className="font-mono">{integration.accountId}</span>}
              {integration.currency && <span>{integration.currency}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div onClick={(e) => e.stopPropagation()}>{renderConnectAction()}</div>
          {canDisconnect && (
            <div onClick={(e) => e.stopPropagation()}>
              <ConfirmPopover
                trigger={
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    disabled={disconnectMutation.isPending}
                  >
                    <Unplug className="size-4" />
                  </Button>
                }
                title={disconnectTitle}
                description={disconnectDescription}
                confirmLabel={t('cards.integrations.facebook.disconnectConfirm', {
                  defaultValue: 'Disconnect',
                })}
                onConfirm={() => disconnectMutation.mutateAsync()}
              />
            </div>
          )}
          <ChevronDown
            className={cn(
              'size-4 text-muted-foreground transition-transform duration-200',
              isExpanded && 'rotate-180'
            )}
          />
        </div>
      </div>

      {/* Animated expanded content */}
      <motion.div style={{ height: animatedHeight }} className="overflow-hidden">
        <div ref={contentRef}>
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="pt-3 space-y-3"
              >
                {/* Error message */}
                {hasError && (
                  <div className="flex items-start gap-1.5 text-xs text-destructive">
                    <AlertTriangle className="size-3 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{integration.lastSyncError}</span>
                  </div>
                )}

                {/* Sync info row */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">{lastSyncedLabel}</span>
                  {integration.lastSyncRecordsCount != null && (
                    <span className="text-xs text-muted-foreground">
                      {t('cards.integrations.sync.records', { count: integration.lastSyncRecordsCount })}
                    </span>
                  )}
                </div>

                {/* Sync actions */}
                {canSync && (
                  <div
                    className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-border"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DateRangePicker
                      value={syncDateRange}
                      onChange={setSyncDateRange}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSync}
                      disabled={syncMutation.isPending || isSyncJobRunning || !syncDateRange?.from || !syncDateRange?.to}
                      className="gap-1.5 shrink-0"
                    >
                      <RefreshCw className={cn('size-3.5', (syncMutation.isPending || isSyncJobRunning) && 'animate-spin')} />
                      {(syncMutation.isPending || isSyncJobRunning)
                        ? t('cards.integrations.sync.syncing')
                        : t('cards.integrations.sync.syncButton')}
                    </Button>
                  </div>
                )}

                {/* Manual CSV import (Meta only; works whether or not the API is connected) */}
                {integration.provider === 'FACEBOOK' && <MetaCsvImportAction />}

                {/* Live sync job status */}
                {syncJob && (
                  <div className="rounded-md border border-border/80 bg-muted/40 p-2 text-xs space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <RefreshCw className={cn('size-3', isSyncJobRunning && 'animate-spin')} />
                        <span>{t('cards.integrations.sync.currentJob', { defaultValue: 'Current sync job' })}</span>
                      </div>
                      <Badge variant={syncJobStatusVariant}>{syncJobStatusLabel}</Badge>
                    </div>
                    {syncJob.message && (
                      <div className="text-muted-foreground">{syncJob.message}</div>
                    )}
                    {syncJob.recordsProcessed != null && (
                      <div className="text-muted-foreground">
                        {t('cards.integrations.sync.records', { count: syncJob.recordsProcessed })}
                      </div>
                    )}
                    {syncJob.error && (
                      <div className="text-destructive">{syncJob.error}</div>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  )
}

/**
 * AdPlatformsCard
 *
 * Displays ad platform integration status (Facebook Ads, Google Ads).
 *
 * @component
 */
export function AdPlatformsCard(): ReactElement {
  const { t } = useTranslation('features-settings')
  const integrationsQuery = useIntegrationsStatus()
  const cardRef = useRef<HTMLDivElement>(null)

  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Filter to only ad platform providers
  const adPlatformIntegrations = (integrationsQuery.data ?? []).filter((integration) =>
    AD_PLATFORM_PROVIDERS.includes(integration.provider)
  )

  const handleToggle = (id: string): void => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  // Collapse all when clicking outside the card
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      if (cardRef.current?.contains(target)) return
      const popoverContent = (target as Element).closest?.('[data-radix-popper-content-wrapper]')
      if (popoverContent) return
      setExpandedId(null)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Card ref={cardRef} className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="size-5" />
          {t('cards.adPlatforms.title', { defaultValue: 'Ad Platforms' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {integrationsQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-lg border border-border">
                <Skeleton className="size-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        ) : integrationsQuery.isError ? (
          <div className="text-center py-8 text-destructive">{t('cards.integrations.loadError')}</div>
        ) : adPlatformIntegrations.length === 0 ? (
          <div className="text-center py-8">
            <Megaphone className="size-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground mb-4">
              {t('cards.adPlatforms.noIntegrations', { defaultValue: 'No ad platforms connected' })}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {adPlatformIntegrations.map((integration, idx) => {
              const entryId = `${integration.provider}-${integration.connectionId ?? idx}`
              return (
                <AdPlatformEntry
                  key={entryId}
                  integration={integration}
                  isExpanded={expandedId === entryId}
                  onToggle={() => handleToggle(entryId)}
                />
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default AdPlatformsCard
